import { Command } from "cmdk";
import { useMemo } from "react";
import { listSymbols, type SymbolDefinition } from "@electro-planner/symbols";

/**
 * Ctrl+K command palette. Sök symboler eller kör redigerarkommandon.
 * cmdk hanterar keyboard-navigering (upp/ner, enter) och fuzzy-filtrering.
 */
export type CommandPaletteCommand = {
  id: string;
  label: string;
  hint?: string;
  shortcut?: string;
  keywords?: string[];
  run: () => void;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: CommandPaletteCommand[];
  onInsertSymbol: (symbol: SymbolDefinition) => void;
};

export function CommandPalette({
  open,
  onOpenChange,
  commands,
  onInsertSymbol,
}: Props) {
  const symbols = useMemo(() => listSymbols(), []);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Kommandopalett"
      className="no-print fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-4 pt-[12vh]"
      shouldFilter
    >
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-[var(--color-border)] bg-white shadow-2xl">
        <Command.Input
          autoFocus
          placeholder="Sök symbol eller kommando…"
          className="w-full border-0 border-b border-[var(--color-border)] bg-transparent px-4 py-3 text-sm outline-none placeholder:text-[var(--color-muted)]"
        />
        <Command.List className="max-h-[50vh] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-8 text-center text-sm text-[var(--color-muted)]">
            Inget matchade.
          </Command.Empty>

          <Command.Group
            heading="Kommandon"
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--color-muted)]"
          >
            {commands.map((cmd) => (
              <Command.Item
                key={cmd.id}
                value={`cmd-${cmd.id} ${cmd.label} ${(cmd.keywords ?? []).join(" ")}`}
                onSelect={() => {
                  cmd.run();
                  onOpenChange(false);
                }}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-sm aria-selected:bg-[var(--color-surface-hover,#f5f5f5)] aria-selected:text-[var(--color-fg)]"
              >
                <span className="flex flex-col">
                  <span>{cmd.label}</span>
                  {cmd.hint ? (
                    <span className="text-xs text-[var(--color-muted)]">{cmd.hint}</span>
                  ) : null}
                </span>
                {cmd.shortcut ? (
                  <kbd className="rounded border border-[var(--color-border)] bg-white px-1.5 text-[10px] font-mono text-[var(--color-muted)]">
                    {cmd.shortcut}
                  </kbd>
                ) : null}
              </Command.Item>
            ))}
          </Command.Group>

          <Command.Group
            heading="Infoga symbol"
            className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--color-muted)]"
          >
            {symbols.map((s) => (
              <Command.Item
                key={s.id}
                value={`sym-${s.id} ${s.label.sv} ${s.familyKey} ${s.designationPrefix} ${(s.searchKeywords ?? []).join(" ")}`}
                onSelect={() => {
                  onInsertSymbol(s);
                  onOpenChange(false);
                }}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm aria-selected:bg-[var(--color-surface-hover,#f5f5f5)]"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[var(--color-border)] bg-white"
                  aria-hidden="true"
                >
                  <svg
                    width={24}
                    height={24}
                    viewBox={s.viewBox}
                    style={{ color: "var(--color-fg, #111827)" }}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: kontrollerat SVG-library
                    dangerouslySetInnerHTML={{ __html: s.svg }}
                  />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{s.label.sv}</span>
                  <span className="truncate text-xs text-[var(--color-muted)]">
                    {s.designationPrefix} · {s.familyKey}
                  </span>
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        </Command.List>
      </div>
    </Command.Dialog>
  );
}
